import React from "react";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "../../../../components/Input/InputWrapper";
import {
  InputLabel,
  RequiredMark,
} from "../../../../components/Input/InputLabel";
import { CustomInput } from "../../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../../components/Input/CustomPhoneInput";
import {
  CountryAutoComplete,
  CountryAutoCompleteItem,
} from "../../../../components/Input/CountryAutoComplete";
import Dropdown from "../../../../components/dropdown/Dropdown";
import {
  FieldGrid,
  FieldGroup,
  ReadOnlyField,
} from "../../../../components/OrgShell/FieldGroup";
import { InputError } from "../../../my-profile/InputError";
import { CustomInputChangeEvent } from "../../../../../utils/models/events/CustomInputChangeEvent";
import { OrganizationUpdateForm } from "../../models/organization-update.form";
import {
  FieldErrorMap,
  useVisibleFieldErrors,
} from "../../models/required-fields";

interface LanguageOption {
  code: string;
  label: string;
}

// Codes are data values (public/locales/{code}) — do not translate them.
export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "en", label: "English" },
  { code: "da", label: "Danish" },
];

// Display names are translated at the render site (the constant values stay
// untouched — they double as fallbacks for unknown codes).
const LANGUAGE_LABEL_KEYS: Record<string, string> = {
  en: "customerUpdate.languageEnglish",
  da: "customerUpdate.languageDanish",
};

interface DetailsSectionProps {
  form: OrganizationUpdateForm;
  onChange: (patch: Partial<OrganizationUpdateForm>) => void;
  navNumber?: string;
  disabled?: boolean;
  isVendor?: boolean;
  /** Marks the fields required (asterisks) — customer edit screen only. */
  required?: boolean;
  /** Required-field errors by field name (customer only). */
  errors?: FieldErrorMap;
  /** Show every error (set after a blocked save attempt). */
  showErrors?: boolean;
}

export function DetailsSection({
  form,
  onChange,
  navNumber,
  disabled,
  isVendor,
  required,
  errors,
  showErrors,
}: DetailsSectionProps) {
  const { t } = useTranslation();
  const { errorFor, markTouched, handleBlur } = useVisibleFieldErrors(
    errors,
    showErrors
  );

  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    onChange({ [target.name]: target.value });
  }

  function handleCountryChange(country: CountryAutoCompleteItem) {
    onChange({ country: country.code });
  }

  function errorClass(field: string): string | undefined {
    return errorFor(field) ? "!border-red-100" : undefined;
  }

  const selectedLanguage = LANGUAGE_OPTIONS.find(
    (x) => x.code === form.language
  );

  return (
    <div className="space-y-8">
      <FieldGroup title={t("customerUpdate.companyDetails")}>
        <FieldGrid>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.companyName")}
              {required && <RequiredMark />}
            </InputLabel>
            <CustomInput
              name="name"
              value={form.name}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("name")}
            />
            <InputError errorMessage={errorFor("name")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>{t("customerUpdate.navNumber")}</InputLabel>
            <ReadOnlyField value={navNumber} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.companyEmail")}
              {required && <RequiredMark />}
            </InputLabel>
            <CustomInput
              type="email"
              name="email"
              value={form.email}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("email")}
            />
            <InputError errorMessage={errorFor("email")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.companyPhone")}
              {required && <RequiredMark />}
            </InputLabel>
            <CustomPhoneInput
              name="phone"
              value={form.phone}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={!!disabled}
              className={errorClass("phone")}
            />
            <InputError errorMessage={errorFor("phone")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.cvr")}
              {required && <RequiredMark />}
            </InputLabel>
            <CustomInput
              name="cvr"
              value={form.cvr}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("cvr")}
            />
            <InputError errorMessage={errorFor("cvr")} />
          </InputWrapper>
          <InputWrapper>
            {/* EORI is optional — no required mark, no validation. */}
            <InputLabel>{t("customerUpdate.eori")}</InputLabel>
            <CustomInput
              name="eori"
              value={form.eori}
              onChange={handleInput}
              disabled={disabled}
            />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      <FieldGroup title={t("customerUpdate.address")}>
        <FieldGrid>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.address")}
              {required && <RequiredMark />}
            </InputLabel>
            <CustomInput
              name="address"
              value={form.address}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("address")}
            />
            <InputError errorMessage={errorFor("address")} />
          </InputWrapper>
          <div className="grid grid-cols-2 gap-x-4">
            <InputWrapper>
              <InputLabel>
                {t("customerUpdate.postcode")}
                {required && <RequiredMark />}
              </InputLabel>
              <CustomInput
                name="postCode"
                value={form.postCode}
                onChange={handleInput}
                onBlur={handleBlur}
                disabled={disabled}
                className={errorClass("postCode")}
              />
              <InputError errorMessage={errorFor("postCode")} />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>
                {t("customerUpdate.city")}
                {required && <RequiredMark />}
              </InputLabel>
              <CustomInput
                name="city"
                value={form.city}
                onChange={handleInput}
                onBlur={handleBlur}
                disabled={disabled}
                className={errorClass("city")}
              />
              <InputError errorMessage={errorFor("city")} />
            </InputWrapper>
          </div>
          <InputWrapper>
            <InputLabel>
              {t("customerUpdate.country")}
              {required && <RequiredMark />}
            </InputLabel>
            <div data-field="country" onBlur={() => markTouched("country")}>
              <CountryAutoComplete
                disabled={!!disabled}
                selectedCode={form.country}
                onCountryChange={handleCountryChange}
                error={!!errorFor("country")}
              />
            </div>
            <InputError errorMessage={errorFor("country")} />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      {!isVendor && (
        <>
          <FieldGroup
            title={t("customerUpdate.language")}
            description={t("customerUpdate.languageDescription")}
          >
            <div className="w-64">
              <Dropdown
                selectedOption={selectedLanguage}
                options={LANGUAGE_OPTIONS}
                defaultTitle={t("customerUpdate.selectLanguage")}
                getOptionDisplay={(option) =>
                  LANGUAGE_LABEL_KEYS[option.code]
                    ? t(LANGUAGE_LABEL_KEYS[option.code])
                    : option.label
                }
                onOptionSelect={(option) => {
                  if (!option) return;
                  onChange({ language: option.code });
                }}
              />
            </div>
          </FieldGroup>

          <FieldGroup title={t("status")}>
            <label className="flex cursor-pointer items-center space-x-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.disabled ?? false}
                onChange={(e) => onChange({ disabled: e.target.checked })}
              />
              <span className="text-sm font-bold text-red">
                {t("customerUpdate.deactivateThisCustomer")}
              </span>
            </label>
            <p className="ml-6 text-xs text-gray-500">
              {t("customerUpdate.deactivateHelp")}
            </p>
          </FieldGroup>
        </>
      )}
    </div>
  );
}
